import { MenuItem, Paper, TextField } from '@mui/material';
import Grid from '@mui/material/Grid'; // MUI v7 Grid (Grid2 API)
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useEffect, useRef } from 'react';
import 'react-grid-layout/css/styles.css';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ButtonVitec from '../../../app/components/ButtonVitec';
import Utils from '../../../felles/Utils';
import MenuItemTarget from '../../../models/MenuItemTarget';
import NN05FormEmployeeData from '../../../models/payload/NN05FormEmployeeData';
import UISize from '../../../models/UISize';
import * as actions from '../../../store/actions/index';
import * as actionsNN05 from '../../../store/actions/indexNN05';
import { AppState } from '../../../store/rootReducer';
import { useStyles } from '../styles/Form.styles';

/**
 * Employee Registration Form Component
 * 
 * A comprehensive form component for adding new employees to the NN05 system.
 * Provides real-time validation, automatic employee ID generation, postal code lookup,
 * and responsive design for both desktop and mobile interfaces.
 * 
 * Author: fabrice.miras@vitecsoftware.com
 * Date: 05.08.2025
 * 
 * Key Features:
 * - Automatic employee ID generation and validation
 * - Real-time postalcode-to-location lookup
 * - Date picker integration with validation constraints
 * - Keyboard navigation and shortcuts
 * - Click-outside detection for form closure
 * - Form state persistence and cleanup
 * - Responsive grid layout with Material-UI
 * 
 * Form Fields:
 * - Employee Number (auto-generated, 3 digits)
 * - Name (required)
 * - Address
 * - Postal Code (4 digits, triggers location lookup)
 * - Location (auto-populated from postalcode)
 * - Phone Number (numeric only, max 8 digits)
 * - Birth Date (date picker with constraints)
 * - Gender (dropdown: M/K)
 * - department (max 4 chars)
 * - Position (max 20 chars)
 * - Employment Start Date
 * - Employment End Date
 * 
 * Validation Rules:
 * - Employee ID must be unique and exactly 3 digits
 * - Name is required
 * - Postal code must be exactly 4 digits
 * - Phone numbers are numeric only
 * - Birth dates are constrained between 1960 and current date
 * - Employment dates are constrained to current year range
 * 
 * @returns {JSX.Element} The employee registration form component
 */
export default function NN05FormAdd(/*props*/) {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const screenSize: UISize = useSelector(state => (state as AppState).ui.screenSize);
  const open = screenSize.width > 0 && screenSize.width < 1200 ? false : true;
  const navigate = useNavigate();
  const n: MenuItemTarget = useSelector((state) => (state as AppState).menuItem.menuItemTarget);
  const isResetFormEnabled: boolean = useSelector((state) => (state as AppState).NN05.isResetFormEnabled);
  const date = useSelector(state => (state as AppState).accounting.date);
  const isLoading: boolean = useSelector((state) => (state as AppState).utils.isLoading);
  const employeeId: string = useSelector((state) => (state as AppState).NN05.employeeId);
  const doesEmployeeIdExist: string = useSelector((state) => (state as AppState).NN05.doesEmployeeIdExist);
  const locationByPostalCode: string = useSelector((state) => (state as AppState).NN05.locationByPostalCode);
  const listFormData: any = useSelector((state) => (state as AppState).NN05.listFormData);
  const button: boolean = useSelector((state) => (state as AppState).ui.isSandwichButtonTurnedOn);

  // Initialize form with null values
  const methods = useForm<NN05FormEmployeeData>({
    defaultValues: {
      employeeId: '000',
      name: '',
      address: '',
      postalCode: '',
      postalLocation: '',
      phoneNumber: '',
      birthDate: dayjs(date).startOf('month'),
      gender: '',
      department: '',
      position: '',
      from: dayjs(date).startOf('month'),
      to: dayjs(date).startOf('month'),
    },
    mode: 'onSubmit', // or 'onChange' if you want validation as you type
  });


  /*
  // Initialize form with default values
  const methods = useForm<NN05FormEmployeeData>({
    defaultValues: {
      employeeId: '000',
      navn: 'pppp',
      adresse: 'Hansteens',
      post: '0253',
      sted: 'Oslo',
      telefon: '97147130',
      birthDate: dayjs(date).startOf('month'),
      kjonn: 'M',
      avdeling: '052',
      stilling: 'none',
      fra: dayjs(date).startOf('month'),
      til: dayjs(date).startOf('month'),
    },
    mode: 'onSubmit', // or 'onChange' if you want validation as you type
  });
  */

  // Destructure errors from formState
  const { errors } = methods.formState;
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * Component initialization and cleanup effect
   * 
   * Handles form initialization, click-outside detection, and automatic employee ID generation.
   * Sets up event listeners and manages form state during the component lifecycle.
   * 
   * Author: fabrice.miras@vitecsoftware.com
   * Date: 05.08.2025 
   * 
   * Initialization Tasks:
   * - Sets up click-outside detection to close the form
   * - Excludes Material-UI popover/menu elements from click-outside behavior
   * - Automatically generates employee ID if not already set
   * - Updates form with generated employee ID
   * 
   * Click-Outside Detection:
   * - Ignores clicks on MUI components (DatePicker, dropdowns, etc.)
   * - Closes form by setting transaction state to 0
   * - Prevents accidental form closure during date/dropdown selection
   * 
   * Employee ID Management:
   * - Requests new employee ID if current value is default ('000')
   * - Updates form field when new ID is received from API
   * - Ensures unique employee identification
   * 
   * Cleanup Tasks:
   * - Resets form state to prevent data leakage
   * - Clears location lookup results
   * - Removes event listeners to prevent memory leaks
   * - Triggers form reset if enabled in settings
   * 
   * Dependencies: [dispatch, isResetFormEnabled, employeeId]
   */
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      // Ignore clicks on MUI popover/menus
      const target = event.target as HTMLElement;
      // MUI menus have role="presentation" or aria attributes, but safest is to check for the menu class
      if (
        target.closest('.MuiPopover-root') ||
        target.closest('.MuiMenu-paper') ||
        target.closest('.MuiMenu-root') ||
        target.closest('.MuiMenu-list') ||
        target.closest('.MuiPickersPopper-root') ||        // DatePicker popper
        target.closest('.MuiDateCalendar-root') ||         // DatePicker calendar
        target.closest('.MuiPickersLayout-root') ||        // DatePicker layout
        target.closest('.MuiDialog-root') ||               // DatePicker dialog (mobile)
        target.closest('.MuiModal-root')
      ) {
        return;
      }
      if (containerRef.current && !containerRef.current.contains(target)) {
        dispatch(actionsNN05.setTransStateInsertEmployee(0));
      }
    }
    document.addEventListener('mousedown', handleClickOutside);

    if (!isLoading && (employeeId === '' || !employeeId)) {
      dispatch(actionsNN05.getEmployeeId());
    }

    if (employeeId !== '') {
      methods.setValue('employeeId', employeeId);
    }
    // On exit
    return () => {
      methods.reset();
      dispatch(actionsNN05.getLocationByPostalCodeSuccess(''));
      document.removeEventListener('mousedown', handleClickOutside);
      if (isResetFormEnabled)
        dispatch(actionsNN05.NN05ResetForm(true));
    };
  }, [dispatch, isResetFormEnabled, employeeId]);

  /**
   * Handles keyboard events for form navigation and actions
   * 
   * Supported keyboard shortcuts:
   * - Shift+F7, F8: Prevented (reserved for pagination in list view)
   * - Shift+End, F3: Closes form and navigates back to main menu
   * - Escape: Cancels the form and closes it
   * 
   * Navigation Actions:
   * - Clears component store when exiting
   * - Resets menu navigation state
   * - Redirects to main menu page
   * 
   * Integration:
   * - Maintains consistent keyboard shortcuts across NN05 components
   * - Provides quick exit paths for experienced users
   * - Preserves menu state for seamless navigation
   * 
   * @param {KeyboardEvent} event - The keyboard event object
   */
  const handlePressedKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const { key } = event;
    if ((event.shiftKey && key === 'F7') || key === 'F8') {
      event.preventDefault();
    }
    if ((event.shiftKey && key === 'End') || key === 'F3') {
      event.preventDefault();
      //  Reset form and component store
      dispatch(actionsNN05.NN05ClearComponentStore());
      dispatch(actions.menuItemSetTarget(new MenuItemTarget(n.rootItem, n.item !== '' ? n.item : n.rootItem, '', '', n.tilpasset, n.prodSynlig
        , n.kodeSynlig, n.labelSynlig, n.menuSynlig, n.hasChildren, n.title)));
      dispatch(actions.menuItemSetSelected([n.rootItem, n.item + n.rootItem]));
      navigate(Utils.HOMEPAGE_CONTEXT + '/menu');
    }
    if (key === 'Enter') { // Handle Escape key

      event.preventDefault();
      console.log('Enter key pressed');
      handleSave();

    }
    if (key === 'Escape' || key === 'Esc') { // Handle Escape key
      event.preventDefault();
      handleCancel();

    }
  }

  /**
   * Cancels the form operation and resets the transaction state
   * 
   * Cleanup Actions:
   * - Sets transaction state to 0 (cancellation)
   * - Closes the form dialog
   * - Returns to the employee list view
   * 
   * Called by:
   * - Cancel button clicks
   * - Escape key presses
   * - Form abort scenarios
   * 
   * State Management:
   * - Updates Redux store to reflect cancellation
   * - Triggers UI state changes to close the form
   */
  const handleCancel = () => {
    dispatch(actionsNN05.getEmployeeIdValidateSuccess({ employeeId: '', message: '' }));
    dispatch(actionsNN05.setTransStateInsertEmployee(0));
  };

  /**
   * Handles form submission for new employee registration
   * 
   * Data Processing:
   * - Validates all form fields using React Hook Form rules
   * - Merges location data from postalcode lookup
   * - Prepares data for API submission
   * 
   * Validation Steps:
   * - Ensures employee ID uniqueness
   * - Validates required fields (name, employee number)
   * - Checks data format consistency
   * 
   * Data Flow:
   * - Combines form data with location information
   * - Dispatches insert action to Redux store
   * - Triggers API call for employee creation
   * 
   * @param {NN05FormEmployeeData | any} data - Form data object containing new employee information
   */
  // Function to handle form submission
  const onSubmit = (data: NN05FormEmployeeData | any) => {
    data.sted = locationByPostalCode;

    if (data.birthDate) {
      data.birthDate = dayjs(data.birthDate).format('YYYY-MM-DD') as any;
    }
    if (data.from) {
      data.from = dayjs(data.from).format('YYYY-MM-DD') as any;
    }
    if (data.to) {
      data.to = dayjs(data.to).format('YYYY-MM-DD') as any;
    }
    dispatch(actionsNN05.getEmployeeIdValidateSuccess({ employeeId: '', message: '' }));
    dispatch(actionsNN05.setTransInsertEmployee({ data, listFormData }));
  };

  /**
   * Initiates the save process by triggering form validation and submission
   * 
   * Process Flow:
   * - Triggers React Hook Form validation for all fields
   * - If validation passes, calls onSubmit with form data
   * - If validation fails, displays error messages to user
   * 
   * Validation Checks:
   * - Employee ID uniqueness and format
   * - Required field completion (name)
   * - Postal code format validation
   * - Data type consistency
   * 
   * Integration:
   * - Connected to save button click events
   * - Uses methods.handleSubmit to leverage form validation
   * - Ensures data integrity before API calls
   */
  // Function to handle save action
  const handleSave = () => {
    methods.handleSubmit(onSubmit)();
  };

  // Render the component
  return (
    <div className={classes.component} >
      <div className={classes.bg} />
      <div className={clsx(classes.content, { [classes.contentShift]: button ? true : open })}>
        <div ref={containerRef} className={classes.root} tabIndex={0} onKeyDown={(e) => handlePressedKey(e)} style={{
        }}>
          <Paper elevation={10} className={classes.canvas}>
            <FormProvider {...methods}>
              <form>
                <Grid container direction="column" sx={{ height: '100%', width: '100%' }}>
                  {/* Header */}
                  <Grid sx={{
                    flex: '0 0 auto',
                    padding: '25px 0 0 0', // Top padding only
                    display: 'flex',
                    alignItems: 'flex-start', // Align to top
                    justifyContent: 'center',
                  }}>
                    {/* Place your header content here */}
                    <h2 style={{ margin: 0 }}>Registrering av persondata</h2>
                  </Grid>
                  {/* Form */}
                  <Grid sx={{ flex: '1 1 auto', padding: 3, display: 'flex', justifyContent: 'center' }}>
                    {/* Place your form fields here */}
                    <Grid container spacing={2.4}>
                      {/* Row 1: Ansatt nr. | Navn */}
                      <Grid size={{xs:12, sm:3}}>
                        <TextField label="AnsattNr." autoComplete="off" type="text" className={classes.tField}
                          {...methods.register("employeeId", {
                            required: "Må være 3 siffer",
                            pattern: {
                              value: /^[0-9]{3}$/,
                              message: "Må være 3 siffer",
                            },
                            validate: (value) => {
                              if (value.length !== 3) return "Må være 3 siffer";
                              if (doesEmployeeIdExist?.length > 0) return doesEmployeeIdExist;
                              return true;
                            },
                            maxLength: 3
                          })}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '');
                            methods.setValue('employeeId', value);
                            methods.trigger("employeeId");
                            //methods.setValue('employeeId', value);
                            //dispatch(actionsNN05.getEmployeeIdValidateSuccess({employeeId: value, message: ''}));
                            if (value.length === 3) {
                              dispatch(actionsNN05.getEmployeeIdValidate(value));
                            }
                          }}
                          InputProps={{ inputProps: { inputMode: 'numeric', pattern: '[0-9]*', maxLength: 3, }, }}
                        />
                      </Grid>
                      <Grid size={{xs:12, sm:9}}>
                        <TextField label="Navn" autoComplete="off" className={classes.tField} error={!!errors.name}
                          autoFocus
                          {...methods.register("name", { required: "Vennligst skriv inn navnet." })}
                          onChange={(e) => {
                            methods.setValue(`name`, (e.target as any).value);
                            methods.trigger("name");
                          }}
                        />
                      </Grid>
                      {/* Row 2: Adresse */}
                      <Grid size={{xs:12}}>
                        <TextField label="Adresse" autoComplete="off" className={classes.tField}
                          {...methods.register("address", { required: false })}
                          onChange={(e) => { methods.setValue(`address`, (e.target as any).value) }}
                        />
                      </Grid>
                      {/* Spacer */}
                      {/* Row 3: Post / Sted */}
                      <Grid size={{xs:12, sm:4}}>
                        <TextField
                          label="Post"
                          autoComplete="off"
                          type="text"
                          className={classes.tField}
                          error={!!errors.postalCode}
                          {...methods.register("postalCode", {
                            required: "Må være 4 siffer",
                            pattern: { value: /^[0-9]{4}$/, message: "Må være 4 siffer" },
                            validate: (value) => {
                              if (value.length !== 4) return "Må være 4 siffer";
                              return true;
                            }, maxLength: 4
                          })}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '');
                            methods.setValue('postalCode', value);
                            methods.trigger("postalCode");
                            if (value.length === 4) {
                              dispatch(actionsNN05.getLocationByPostalCode(value));
                            } else {
                              dispatch(actionsNN05.getLocationByPostalCodeSuccess(''));
                            }
                          }}
                          InputProps={{ inputProps: { inputMode: 'numeric', pattern: '[0-9]*', maxLength: 4 } }}
                        />
                      </Grid>
                      <Grid size={{xs:12, sm:8}}>
                        <input type="hidden" {...methods.register("postalLocation")} name="postalLocation"
                          onChange={(e) => { methods.setValue(`postalLocation`, locationByPostalCode) }}
                        />
                        <TextField type="text" className={classes.tField} value={locationByPostalCode} InputProps={{
                          readOnly: true,
                          disableUnderline: false,
                          sx: {
                            border: 'none', background: 'transparent', fontSize: '1.3em', marginTop: 1, alignSelf: 'flex-end',
                          }
                        }}
                          variant="standard" fullWidth />
                      </Grid>
                      {/* Row 4: Telefon | Fødselsdato | Kjønn */}
                      <Grid container spacing={2} alignItems="flex-end" sx={{ width: '100%', marginLeft: 0, marginTop: 0 }}>
                        <Grid size={{xs:12, sm:4}}>
                          <TextField label="Telefon" autoComplete="off" type="text" className={classes.tField}
                            {...methods.register("phoneNumber", { required: false, pattern: /^[0-9]*$/, maxLength: 10 })}
                            onChange={(e) => { const value = e.target.value.replace(/[^0-9]/g, ''); methods.setValue('phoneNumber', value); }}
                            InputProps={{ inputProps: { inputMode: 'numeric', pattern: '[0-9]*', maxLength: 8, }, }}
                          />
                        </Grid>
                        <Grid size={{xs:12, sm:5}}>
                          <Controller
                            control={methods.control}
                            name={`birthDate`}
                            rules={{ required: "Fødselsdato er påkrevd" }}
                            render={({ field: { onChange/*, value*/ } }) => (
                              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nb">
                                <DatePicker
                                  label="Fødselsdato"
                                  sx={{ width: '100%', marginTop: '8px' }}
                                  className={classes.tField}
                                  openTo="day"
                                  disableFuture={false}
                                  minDate={dayjs().set('year', 1960).set('month', 0)}
                                  maxDate={dayjs(date)}
                                  value={dayjs(methods.getValues('birthDate'))}
                                  views={["year", "month", "day"]}
                                  onChange={(item) => {
                                    // Ensure we keep the date as-is without timezone conversion
                                    const normalizedDate = item ? item.startOf('day') : null;
                                    onChange(normalizedDate);
                                    methods.setValue('birthDate', normalizedDate as any);
                                  }}
                                />
                              </LocalizationProvider>
                            )}
                          />
                        </Grid>
                        <Grid size={{xs:12, sm:3}}>
                          <Controller
                            control={methods.control}
                            name="gender"
                            rules={{ required: "Kjønn er påkrevd" }}
                            render={({ field }) => (
                              <TextField
                                select
                                label="Kjønn"
                                autoComplete="off"
                                className={classes.tField}
                                error={!!errors.gender}
                                value={field.value || ""}
                                onChange={field.onChange}
                                InputProps={{ inputProps: { maxLength: 1 } }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MenuItem value="M">M</MenuItem>
                                <MenuItem value="K">K</MenuItem>
                              </TextField>
                            )}
                          />
                        </Grid>
                      </Grid>
                      <Grid size={{xs:9, sm:4}}>
                        <TextField label="Avdeling" autoComplete="off" className={classes.tField}
                          error={!!errors.department}
                          {...methods.register("department", { required: "Avdeling er påkrevd" })}
                          onChange={(e) => {
                            methods.setValue(`department`, (e.target as any).value);
                            methods.trigger("department");
                          }}
                          InputProps={{ inputProps: { maxLength: 4 } }}
                        />
                      </Grid>
                      {/* Row 5: Avdeling | Stilling */}
                      <Grid size={{xs:15, sm:8}}>
                        <TextField label="Stilling" autoComplete="off" className={classes.tField}
                          {...methods.register("position", { required: false })}
                          onChange={(e) => { methods.setValue(`position`, (e.target as any).value) }}
                          InputProps={{ inputProps: { maxLength: 20 } }}
                        />
                      </Grid>
                      {/* Row 6: Ansatt dato | Sluttet dato */}
                      <Grid size={{xs:12, sm:6}}>
                        <Controller control={methods.control} name={`from`} render={({ field: { onChange/*, value*/ } }) => (
                          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nb">
                            <DatePicker
                              label="Ansatt dato"
                              sx={{ width: '100%', marginTop: '8px' }}
                              className={classes.tField}
                              openTo="day"
                              disableFuture={false}
                              minDate={dayjs().set('year', 2024).set('month', 0)}
                              maxDate={dayjs(date).add(1, 'year').endOf('year')}
                              value={dayjs(methods.getValues('from'))}
                              views={["year", "month", "day"]}
                              onChange={(item) => {
                                const normalizedDate = item ? item.startOf('day') : null;
                                onChange(normalizedDate);
                                methods.setValue('from', normalizedDate as any);
                              }}
                            />
                          </LocalizationProvider>
                        )}
                        />
                      </Grid>
                      {/* 
                      <Grid item xs={12} sm={6}>
                        <Controller control={methods.control} name={`til`} render={({ field: { onChange} }) => (
                          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nb">
                            <DatePicker
                              label="Sluttet dato"
                              sx={{ width: '100%', marginTop: '8px' }}
                              className={classes.tField}
                              openTo="day"
                              disableFuture={false}
                              minDate={dayjs().set('year', 2024).set('month', 0)}
                              maxDate={dayjs(date).add(1, 'year').endOf('year')}
                              value={dayjs(methods.getValues('til'))}
                              views={["year", "month", "day"]}
                              onChange={(item) => {
                                const normalizedDate = item ? item.startOf('day') : null;
                                onChange(normalizedDate);
                                methods.setValue('til', normalizedDate as any);
                              }}
                            />
                          </LocalizationProvider>
                        )}
                        />
                      </Grid>
                      */}
                    </Grid>
                  </Grid>
                  {/* Footer */}
                  <Grid sx={{
                    flex: '0 0 auto', marginTop: 'auto', paddingBottom: 2, display: 'flex', justifyContent: 'center',
                    alignItems: 'center',
                  }} className={classes.formError}>
                    {errors.employeeId
                      ? <div>{errors.employeeId.message}</div>
                      : doesEmployeeIdExist.length > 0 && methods.getValues('employeeId').length === 3
                        ? <div>{doesEmployeeIdExist}</div>
                        : errors.name
                          ? <div>Vennligst skriv inn navnet.</div>
                          : errors.postalCode
                            ? <div>{errors.postalCode.message}</div>
                            : errors.birthDate
                              ? <div>{errors.birthDate.message}</div>
                              : errors.gender
                                ? <div>{errors.gender.message}</div>
                                : errors.department
                                  ? <div>{errors.department.message}</div>
                                  : <div>&nbsp;</div>
                    }
                  </Grid>
                  <Grid sx={{
                    flex: '0 0 auto', marginTop: 'auto', padding: 0, display: 'flex', justifyContent: 'center',
                    alignItems: 'top', gap: 5, height: 60, width: '100%',
                  }}>
                    {/* Place your footer actions here */}
                    <ButtonVitec _label="Lagre" _type="button" _padding={0} _onclick={handleSave} _tooltip={'Enter'} />
                    <ButtonVitec _label="Avbryt" _type="button" _padding={0} _onclick={handleCancel} _tooltip={'Esc.'} />
                  </Grid>
                </Grid>
              </form>
            </FormProvider>
          </Paper>
        </div>
      </div>
    </div >
  );
}